"use client";

import React from "react";
import { Mail, Linkedin, Globe, MapPin, CircleCheck, Loader, Send, Building2, Phone } from "lucide-react";
import { useWatch } from "react-hook-form";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const Input = React.forwardRef<HTMLInputElement, React.InputHTMLAttributes<HTMLInputElement>>(({
  className,
  ...props
}, ref) => {
  return <input className={`flex h-12 w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Input.displayName = "Input";
const Textarea = React.forwardRef<HTMLTextAreaElement, React.TextareaHTMLAttributes<HTMLTextAreaElement>>(({
  className,
  ...props
}, ref) => {
  return <textarea className={`flex min-h-[120px] w-full rounded-sm border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props} />;
});
Textarea.displayName = "Textarea";
const Select = React.forwardRef<HTMLSelectElement, React.SelectHTMLAttributes<HTMLSelectElement>>(({
  className,
  children,
  ...props
}, ref) => {
  return <div className="relative">
        <select className={`flex h-12 w-full appearance-none rounded-sm border border-input bg-background/50 px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 ${className}`} ref={ref} {...props}>
          {children}
        </select>
        <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-muted-foreground">
          <svg className="h-4 w-4 fill-current" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20">
            <path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" />
          </svg>
        </div>
      </div>;
});
Select.displayName = "Select";
const Progress = ({
  value,
  title,
  subtitle
}: {
  value: number;
  title: string;
  subtitle: string;
}) => {
  const getColor = (v: number) => {
    if (v <= 30) return "bg-destructive";
    if (v <= 70) return "bg-orange-500";
    return "bg-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.4)]";
  };
  return <div className="mb-8 space-y-3 p-4 rounded-sm bg-primary/5 border border-primary/10 backdrop-blur-sm">
      <div className="flex justify-between items-center">
        <div className="space-y-0.5">
          <p className="text-[10px] font-mono uppercase tracking-widest text-primary/60">{title}</p>
          
        </div>
        <span className="text-xs font-mono font-bold text-primary">{value}%</span>
      </div>
      <div className="h-1 w-full bg-white/5 rounded-full overflow-hidden">
        <div className={`h-full transition-all duration-1000 ease-in-out ${getColor(value)}`} style={{
        width: `${value}%`
      }} />
      </div>
    </div>;
};
function ContactForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const [completion, setCompletion] = React.useState(0);
  const watchedFields = useWatch({
    control
  });
  React.useEffect(() => {
    const fields = ["name", "company", "email", "phone", "preferred_contact", "project_description"];
    const filledCount = fields.filter(field => {
      const val = watchedFields?.[field];
      return val && val.toString().trim() !== "";
    }).length;
    const percentage = Math.round(filledCount / 6 * 100);
    setCompletion(Math.min(99, percentage));
  }, [watchedFields]);
  const handleFormSubmit = async (e: React.FormEvent) => {
    setCompletion(100);
    await handleSubmit(e);
  };
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center space-y-6 bg-card/30 border border-border/50 rounded-lg backdrop-blur-sm animate-in fade-in zoom-in duration-500">
        <div className="h-20 w-20 rounded-full bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="h-10 w-10 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground">Messaggio Inviato</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-6 border-primary/20 hover:bg-primary/10 hover:text-primary">
          Invia un altro messaggio
        </Button>
      </div>;
  }
  return <>
      <Progress title="Quanto è pronta la tua infrastruttura IT?" subtitle="Completa la richiesta per ricevere una consulenza tecnica mirata." value={completion} />
      <Form onSubmit={handleFormSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" rules={{
          required: "Il nome è obbligatorio"
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/80">Nome e Cognome <span className="text-primary">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="Mario Rossi" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="company" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/80">Azienda / Organizzazione</FormLabel>
                <FormControl>
                  <Input placeholder="B-Tech" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="email" rules={{
          required: "L'email è obbligatoria",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Inserisci un indirizzo email valido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/80">Email <span className="text-primary">*</span></FormLabel>
                <FormControl>
                  <Input type="email" placeholder="mario.rossi@example.com" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="phone" rules={{
          pattern: {
            value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
            message: "Numero di telefono non valido"
          }
        }} render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/80">Telefono</FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="+39 333 1234567" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="budget_range" render={({
          field
        }) => {}} />

          <FormField control={control} name="preferred_contact" render={({
          field
        }) => <FormItem>
                <FormLabel className="text-foreground/80">Contatto Preferito</FormLabel>
                <FormControl>
                  <div className="flex gap-4 h-12 items-center">
                    {["Email", "Telefono", "Video Call"].map(option => <label key={option} className="flex items-center gap-2 cursor-pointer text-sm text-muted-foreground hover:text-foreground transition-colors">
                        <input type="radio" value={option} checked={field.value === option} onChange={field.onChange} className="accent-primary h-4 w-4" />
                        {option}
                      </label>)}
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="project_description" rules={{
        required: "Descrivi brevemente il tuo progetto",
        minLength: {
          value: 20,
          message: "La descrizione deve essere di almeno 20 caratteri"
        },
        maxLength: {
          value: 1000,
          message: "La descrizione non può superare i 1000 caratteri"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80">Descrizione del Progetto <span className="text-primary">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="Spiega brevemente le tue esigenze, obiettivi e tempistiche..." {...field} />
              </FormControl>
              <FormDescription className="text-xs text-muted-foreground/60 text-right">
                {field.value?.length || 0}/1000 caratteri
              </FormDescription>
              <FormMessage />
            </FormItem>} />

        {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg animate-in fade-in slide-in-from-top-2">
            <p className="text-destructive text-sm font-medium flex items-center gap-2">
              <span className="text-lg"></span> {submitError}
            </p>
          </div>}

        <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-semibold bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_20px_-5px_var(--color-primary)] transition-all duration-300">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 h-5 w-5" />
              Invio in corso...
            </> : <>
              Invia Richiesta <Send className="ml-2 h-4 w-4" />
            </>}
        </Button>
        
        <p className="text-xs text-center text-muted-foreground pt-2">
          I tuoi dati sono trattati nel rispetto della privacy policy.
        </p>
      </Form>
    </>;
}
export default function ParliamoDelTuoProgetto() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="parliamo-del-tuo-progetto" className="dark bg-background text-foreground py-20 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-10%] w-[600px] h-[600px] bg-secondary/10 rounded-full blur-[120px]" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://wpvc-images.s3.us-east-1.amazonaws.com/images/1570363/img/circuit_pattern_dark.png')] opacity-[0.03] bg-repeat" />
      </div>

      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
          
          {}
          <div className="flex flex-col justify-center space-y-10">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20 text-primary text-xs font-medium tracking-wider uppercase">
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-primary"></span>
                </span>
                Disponibile per nuovi progetti
              </div>
              
              <h2 className="text-4xl md:text-5xl font-bold tracking-tight text-foreground">
                Parliamo del tuo <span className="text-primary">Progetto IT</span>
              </h2>
              
              <p className="text-lg text-muted-foreground leading-relaxed max-w-xl">
                Hai bisogno di una consulenza tecnica indipendente per la tua infrastruttura o i tuoi dispositivi aziendali? 
                Compila il modulo per richiedere una valutazione preliminare.
              </p>
            </div>

            <Separator className="bg-border/50" />

            <div className="space-y-8">
              <div className="flex items-start gap-4 group">
                <div className="h-12 w-12 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors duration-300">
                  <Mail className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-1">Email&nbsp;</h4>
                  <p className="text-muted-foreground text-sm mb-2">Per richieste generali e informazioni.</p>
                  <Link to="mailto:info@b-tech.it" className="text-primary hover:text-primary/80 font-medium transition-colors">info@b-tech.it</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="h-12 w-12 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors duration-300">
                  <Linkedin className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-1">LinkedIn</h4>
                  <p className="text-muted-foreground text-sm mb-2">Connettiamoci professionalmente.</p>
                  <Link to="https://www.linkedin.com/company/b-techit" newTab className="text-primary hover:text-primary/80 font-medium transition-colors">B-Tech IT</Link>
                </div>
              </div>

              <div className="flex items-start gap-4 group">
                <div className="h-12 w-12 rounded-lg bg-card border border-border flex items-center justify-center shrink-0 group-hover:border-primary/50 group-hover:bg-primary/5 transition-colors duration-300">
                  <Phone className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h4 className="text-base font-semibold text-foreground mb-1">Telefono</h4>
                  <Link to="tel:+393770884303" className="text-primary hover:text-primary/80 font-medium transition-colors">+39 377 088 4303</Link>
                </div>
              </div>
            </div>

            
          </div>

          {}
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-b from-primary/5 to-transparent rounded-xl blur-xl -z-10" />
            <div className="bg-card/40 backdrop-blur-md border border-white/10 rounded-xl p-6 md:p-8 shadow-2xl">
              <div className="mb-8">
                <h3 className="text-2xl font-bold text-foreground mb-2">Richiedi Consulenza</h3>
                <p className="text-muted-foreground text-sm">
                  Compila il form con i dettagli del tuo progetto. Riceverai una risposta tecnica entro 24 ore lavorative.
                </p>
              </div>

              <FormProvider formId="project_inquiry_form_contacts" sectionName="contact-section" defaultValues={{
              name: "",
              company: "",
              email: "",
              phone: "",
              project_description: "",
              budget_range: "",
              preferred_contact: "Email"
            }} validationRules={{
              name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              project_description: {
                required: true,
                minLength: 20,
                maxLength: 1000
              }
            }} submitText="Invia Richiesta" successMessage="Grazie per averci contattato. La tua richiesta è stata ricevuta e verrà analizzata a breve." errorMessage="Si è verificato un errore durante l'invio. Per favore riprova o contattaci via email.">
                <ContactForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}